import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	CalendarDays,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import "./DateRangePicker.css";

const WEEK_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const startOfDay = (date) => {
	const next = new Date(date);
	next.setHours(0, 0, 0, 0);
	return next;
};

const addDays = (date, days) => {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
};

const sameDay = (a, b) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();

const isBefore = (a, b) => startOfDay(a).getTime() < startOfDay(b).getTime();

const isInRange = (day, from, to) => {
	if (!from || !to) return false;
	const time = startOfDay(day).getTime();
	return (
		time >= startOfDay(from).getTime() && time <= startOfDay(to).getTime()
	);
};

// Local-time parsing/formatting so a "YYYY-MM-DD" value never shifts a day.
const parseISO = (value) => {
	if (!value) return null;
	const [year, month, day] = String(value).split("-").map(Number);
	if (!year || !month || !day) return null;
	return new Date(year, month - 1, day);
};

const toISO = (date) => {
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
};

const formatShort = (date) =>
	date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});

// Only the viewed month's own days; leading slots are blanks so the first
// date still lands under its weekday column.
const getMonthCells = (view) => {
	const year = view.getFullYear();
	const month = view.getMonth();
	const leading = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const cells = [];

	for (let i = 0; i < leading; i += 1) {
		cells.push(null);
	}
	for (let day = 1; day <= daysInMonth; day += 1) {
		cells.push(new Date(year, month, day));
	}

	return cells;
};

const MonthCalendar = ({
	view,
	onPrev,
	onNext,
	showPrev,
	showNext,
	draftFrom,
	draftTo,
	minDate,
	maxDate,
	onSelectDay,
}) => {
	const cells = useMemo(() => getMonthCells(view), [view]);
	const today = startOfDay(new Date());

	return (
		<div className="mp-cal">
			<div className="mp-cal-head">
				<button
					type="button"
					className={`mp-cal-nav ${showPrev ? "" : "is-hidden"}`}
					onClick={onPrev}
					disabled={!showPrev}
					aria-label="Previous month"
				>
					<ChevronLeft size={16} aria-hidden />
				</button>
				<p className="mp-cal-title">
					{view.toLocaleDateString("en-US", {
						month: "long",
						year: "numeric",
					})}
				</p>
				<button
					type="button"
					className={`mp-cal-nav ${showNext ? "" : "is-hidden"}`}
					onClick={onNext}
					disabled={!showNext}
					aria-label="Next month"
				>
					<ChevronRight size={16} aria-hidden />
				</button>
			</div>

			<div className="mp-cal-weekdays">
				{WEEK_DAYS.map((day) => (
					<div key={day}>{day}</div>
				))}
			</div>

			<div className="mp-cal-grid">
				{cells.map((day, index) => {
					if (!day) {
						// eslint-disable-next-line react/no-array-index-key
						return <span key={`blank-${index}`} className="mp-cal-blank" />;
					}

					const isStart = draftFrom ? sameDay(day, draftFrom) : false;
					const isEnd = draftTo ? sameDay(day, draftTo) : false;
					const isEdge = isStart || isEnd;
					const inSelected = isInRange(day, draftFrom, draftTo);
					const disabled =
						(minDate && isBefore(day, minDate)) ||
						(maxDate && isBefore(maxDate, day));

					const classes = [
						"mp-cal-day",
						inSelected && !isEdge ? "is-in-range" : "",
						isEdge ? "is-selected" : "",
						sameDay(day, today) && !isEdge ? "is-today" : "",
						disabled ? "is-disabled" : "",
					]
						.filter(Boolean)
						.join(" ");

					return (
						<button
							key={day.toISOString()}
							type="button"
							className={classes}
							disabled={disabled}
							onClick={() => onSelectDay(startOfDay(day))}
						>
							{day.getDate()}
						</button>
					);
				})}
			</div>
		</div>
	);
};

const DateRangePicker = ({
	startDate,
	endDate,
	onApply,
	minDate,
	maxDate,
	label = "Custom range",
	placeholder = "Select date range",
}) => {
	const rootRef = useRef(null);
	const [open, setOpen] = useState(false);
	const [draftFrom, setDraftFrom] = useState(null);
	const [draftTo, setDraftTo] = useState(null);
	const [leftMonth, setLeftMonth] = useState(() =>
		startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
	);

	const selectedFrom = parseISO(startDate);
	const selectedTo = parseISO(endDate);
	const min = parseISO(minDate);
	const max = parseISO(maxDate);

	const rightMonth = useMemo(
		() => new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1),
		[leftMonth]
	);

	useEffect(() => {
		if (!open) return undefined;

		const onPointerDown = (event) => {
			if (!rootRef.current?.contains(event.target)) setOpen(false);
		};
		const onKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};

		document.addEventListener("mousedown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("mousedown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open]);

	const openMenu = () => {
		if (!open) {
			const anchor = selectedFrom || max || new Date();
			setDraftFrom(selectedFrom);
			setDraftTo(selectedTo);
			setLeftMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
		}
		setOpen((prev) => !prev);
	};

	const handleDaySelect = (day) => {
		if (!draftFrom || draftTo || isBefore(day, draftFrom)) {
			setDraftFrom(day);
			setDraftTo(null);
			return;
		}
		setDraftTo(day);
	};

	const shiftMonth = (offset) =>
		setLeftMonth(
			new Date(leftMonth.getFullYear(), leftMonth.getMonth() + offset, 1)
		);

	const applyRange = () => {
		if (!draftFrom || !draftTo) return;
		onApply(toISO(draftFrom), toISO(draftTo));
		setOpen(false);
	};

	const triggerLabel =
		selectedFrom && selectedTo
			? `${formatShort(selectedFrom)} – ${formatShort(selectedTo)}`
			: placeholder;

	const draftLabel = () => {
		if (draftFrom && draftTo)
			return `${formatShort(draftFrom)} – ${formatShort(draftTo)}`;
		if (draftFrom) return `${formatShort(draftFrom)} – Select end date`;
		return "Select a start date";
	};

	return (
		<div className="mp-range" ref={rootRef}>
			<button
				type="button"
				className={`mp-range-trigger ${
					selectedFrom && selectedTo ? "is-active" : ""
				}`}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={openMenu}
			>
				<CalendarDays size={15} aria-hidden />
				<span className="mp-range-value">{triggerLabel}</span>
				<ChevronDown
					size={15}
					aria-hidden
					className={`mp-range-caret ${open ? "is-open" : ""}`}
				/>
			</button>

			{open && (
				<div className="mp-range-panel" role="dialog" aria-label={label}>
					<div className="mp-range-panel-head">
						<div>
							<p className="mp-range-panel-title">{label}</p>
							<p className="mp-range-panel-sub">{draftLabel()}</p>
						</div>
						<button
							type="button"
							className="mp-range-clear"
							onClick={() => {
								setDraftFrom(null);
								setDraftTo(null);
							}}
						>
							Clear
						</button>
					</div>

					<div className="mp-range-months">
						<MonthCalendar
							view={leftMonth}
							onPrev={() => shiftMonth(-1)}
							onNext={() => shiftMonth(1)}
							showPrev
							showNext={false}
							draftFrom={draftFrom}
							draftTo={draftTo}
							minDate={min}
							maxDate={max}
							onSelectDay={handleDaySelect}
						/>
						<div className="mp-range-divider" />
						<MonthCalendar
							view={rightMonth}
							onPrev={() => shiftMonth(-1)}
							onNext={() => shiftMonth(1)}
							showPrev={false}
							showNext
							draftFrom={draftFrom}
							draftTo={draftTo}
							minDate={min}
							maxDate={max}
							onSelectDay={handleDaySelect}
						/>
					</div>

					<div className="mp-range-footer">
						<button
							type="button"
							className="mp-range-btn"
							onClick={() => setOpen(false)}
						>
							Cancel
						</button>
						<button
							type="button"
							className="mp-range-btn is-primary"
							disabled={!draftFrom || !draftTo}
							onClick={applyRange}
						>
							Apply range
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default DateRangePicker;
